import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import {
  ManageNewsPayload,
  NewsCategory,
  NewsDetail,
  NewsImage,
} from '../../../../../core/models/cityparking/news.model';
import { ApiNewsService } from '../../../../../core/services/api/cityparking/api-news.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../../core/services/site-state.service';
import { forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ModalAdminActivityHistoryComponent } from '../../system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-news',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-news.component.html',
  styleUrl: './modal-news.component.css',
})
export class ModalNewsComponent implements OnInit {
  @Input() editId: number | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiNewsService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  private static readonly MaxGallery = 7;

  categories = signal<NewsCategory[]>([]);
  galleryImages = signal<NewsImage[]>([]);
  isSaving = signal(false);
  isLoading = signal(false);
  isUploadingGallery = signal(false);
  coverPreview = signal<string | null>(null);
  coverBase64: string | null = null;
  isCoverDragOver = false;

  /** base64 pending upload (create mode or before save) */
  pendingGallery: { fileName: string; base64: string; preview: string }[] = [];

  form: ManageNewsPayload = this.emptyForm();

  openDropdown: 'category' | 'site' | null = null;
  searchCategoryText = '';
  searchSiteText = '';
  private blurTimer: ReturnType<typeof setTimeout> | null = null;

  get isEdit(): boolean {
    return (this.editId ?? 0) > 0;
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get galleryCount(): number {
    return this.galleryImages().length + this.pendingGallery.length;
  }

  get canAddGallery(): boolean {
    return this.galleryCount < ModalNewsComponent.MaxGallery;
  }

  get selectedCategoryLabel(): string {
    const cat = this.categories().find((c) => c.id === this.form.categoryId);
    return cat ? `${cat.name} (${cat.code})` : '';
  }

  get filteredCategories(): NewsCategory[] {
    const q = this.searchCategoryText.trim().toLowerCase();
    const list = this.categories();
    if (!q || this.searchCategoryText === this.selectedCategoryLabel) return list;
    return list.filter(
      (c) =>
        c.name.toLowerCase().includes(q)
        || c.code.toLowerCase().includes(q)
        || String(c.id).includes(q),
    );
  }

  /** ข่าว public ที่ผูก site — เลือกได้ว่าจะขึ้น Home / ข่าวรวมด้วยไหม */
  get isSiteNews(): boolean {
    return (this.form.siteId ?? 0) > 0 && this.form.visibility !== 'internal';
  }

  get selectedSiteLabel(): string {
    if (this.form.siteId == null || this.form.siteId <= 0) {
      return 'ทุก site (Global)';
    }
    const site = this.siteState.sites().find((s) => s.id === this.form.siteId);
    return site?.name || `Site #${this.form.siteId}`;
  }

  get filteredSites(): { id: number; name: string }[] {
    const q = this.searchSiteText.trim().toLowerCase();
    const list = this.siteState.sites().map((s) => ({ id: s.id, name: s.name }));
    if (!q || this.searchSiteText === this.selectedSiteLabel) return list;
    return list.filter(
      (s) => s.name.toLowerCase().includes(q) || String(s.id).includes(q),
    );
  }

  ngOnInit(): void {
    this.form = this.emptyForm();
    this.searchCategoryText = '';
    this.searchSiteText = this.selectedSiteLabel;
    this.api.getNewsCategories(true).subscribe({
      next: (cats) => {
        this.categories.set(cats ?? []);
        if (this.form.categoryId) {
          this.searchCategoryText = this.selectedCategoryLabel;
        }
      },
      error: () => this.toast.error('โหลดประเภทข่าวไม่สำเร็จ'),
    });

    if (this.isEdit && this.editId) {
      this.loadDetail(this.editId);
    }
  }

  onClose(): void {
    this.close.emit();
  }

  onInputFocus(field: 'category' | 'site'): void {
    this.openDropdown = field;
  }

  closeDropdowns(): void {
    if (this.blurTimer) clearTimeout(this.blurTimer);
    this.blurTimer = setTimeout(() => {
      this.openDropdown = null;
      this.revertTypedDropdowns();
    }, 150);
  }

  selectCategory(cat: NewsCategory): void {
    this.form.categoryId = cat.id;
    this.searchCategoryText = `${cat.name} (${cat.code})`;
    this.openDropdown = null;
  }

  selectSite(siteId: number | null, label: string): void {
    this.form.siteId = siteId && siteId > 0 ? siteId : null;
    this.searchSiteText = label;
    this.openDropdown = null;
  }

  onUseCurrentSite(): void {
    const site = this.siteState.selectedSite();
    if (!site?.id) {
      this.toast.error('ยังไม่ได้เลือกลานจาก Sidebar');
      return;
    }
    this.selectSite(site.id, site.name);
  }

  onClearSite(): void {
    this.selectSite(null, 'ทุก site (Global)');
  }

  private revertTypedDropdowns(): void {
    this.searchCategoryText = this.selectedCategoryLabel;
    this.searchSiteText = this.selectedSiteLabel;
  }

  onCoverSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.applyCoverFile(file);
    input.value = '';
  }

  onCoverDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isCoverDragOver = true;
  }

  onCoverDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isCoverDragOver = true;
  }

  onCoverDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isCoverDragOver = false;
  }

  onCoverDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isCoverDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    this.applyCoverFile(file);
  }

  clearCoverSelection(): void {
    this.coverBase64 = null;
    if (this.form.coverImageUrl) {
      this.coverPreview.set(this.form.coverImageUrl);
    } else {
      this.coverPreview.set(null);
    }
  }

  onGallerySelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (!files.length) return;

    for (const file of files) {
      if (!this.canAddGallery) {
        this.toast.error(`แนบรูปในเนื้อหาได้สูงสุด ${ModalNewsComponent.MaxGallery} รูป`);
        break;
      }
      if (!file.type.startsWith('image/')) {
        this.toast.error('กรุณาเลือกไฟล์รูป');
        continue;
      }
      this.readGalleryFile(file);
    }
  }

  removePendingGallery(index: number): void {
    this.pendingGallery.splice(index, 1);
  }

  deactivateGalleryImage(img: NewsImage): void {
    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }
    if (!this.permissionService.canUpdate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }

    this.isUploadingGallery.set(true);
    this.api.deactivateNewsImage({ userId, menuId, id: img.id }).subscribe({
      next: (res) => {
        this.isUploadingGallery.set(false);
        if (res?.isSuccess) {
          this.galleryImages.update((list) => list.filter((x) => x.id !== img.id));
          this.toast.success(res.message || 'ลบรูปแล้ว');
        } else {
          this.toast.error(res?.message || 'ลบรูปไม่สำเร็จ');
        }
      },
      error: () => {
        this.isUploadingGallery.set(false);
        this.toast.error('ลบรูปไม่สำเร็จ');
      },
    });
  }

  private applyCoverFile(file: File): void {
    if (!file.type.startsWith('image/')) {
      this.toast.error('กรุณาเลือกไฟล์รูป');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      this.coverBase64 = dataUrl;
      this.coverPreview.set(dataUrl);
    };
    reader.readAsDataURL(file);
  }

  private readGalleryFile(file: File): void {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      if (!this.canAddGallery) return;
      this.pendingGallery.push({
        fileName: file.name,
        base64: dataUrl,
        preview: dataUrl,
      });
    };
    reader.readAsDataURL(file);
  }

  onSave(): void {
    if (!this.form.title?.trim()) {
      this.toast.error('กรุณาระบุหัวข้อ');
      return;
    }
    if (!this.form.categoryId) {
      this.toast.error('กรุณาเลือกประเภทข่าว');
      return;
    }
    // ถ้าพิมพ์ค้นหาแต่ไม่กดเลือก ให้ sync กลับค่าที่เลือกไว้
    this.revertTypedDropdowns();
    if (!this.form.categoryId) {
      this.toast.error('กรุณาเลือกประเภทข่าวจากรายการ');
      return;
    }

    if (this.isEdit && !this.permissionService.canUpdate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (!this.isEdit && !this.permissionService.canCreate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    const payload: ManageNewsPayload = {
      ...this.form,
      userId,
      menuId,
      id: this.editId ?? 0,
      title: this.form.title.trim(),
      visibility: this.form.visibility === 'internal' ? 'internal' : 'public',
      siteId: this.form.siteId && this.form.siteId > 0 ? this.form.siteId : null,
      showOnGlobal: this.form.siteId && this.form.siteId > 0 ? this.form.showOnGlobal !== false : true,
      publishFrom: this.form.publishFrom || null,
      publishTo: this.form.publishTo || null,
      coverImageBase64: this.coverBase64,
    };

    this.isSaving.set(true);
    this.api.manageNews(payload).pipe(
      switchMap((res) => {
        if (!res?.isSuccess) {
          return of({ ok: false as const, message: res?.message || 'บันทึกไม่สำเร็จ', newsId: 0 });
        }
        const newsId =
          this.editId && this.editId > 0
            ? this.editId
            : Number(
                (res as { IdReturn?: number; idReturn?: number }).IdReturn
                  ?? (res as { idReturn?: number }).idReturn
                  ?? 0,
              );
        if ((!newsId || newsId <= 0) && this.pendingGallery.length > 0) {
          return of({
            ok: true as const,
            message: (res.message || 'บันทึกข่าวแล้ว') + ' แต่ไม่พบ id สำหรับอัปโหลดรูป',
            newsId: 0,
          });
        }
        if (!newsId || this.pendingGallery.length === 0) {
          return of({ ok: true as const, message: res.message || 'บันทึกสำเร็จ', newsId });
        }
        this.isUploadingGallery.set(true);
        const uploads = this.pendingGallery.map((g, index) =>
          this.api.uploadNewsImage({
            userId,
            menuId,
            newsId,
            fileName: g.fileName,
            sortOrder: this.galleryImages().length + index,
            base64: g.base64,
          }).pipe(catchError(() => of({ isSuccess: false, message: 'อัปโหลดรูปไม่สำเร็จ' } as never))),
        );
        return forkJoin(uploads).pipe(
          switchMap((results) => {
            const failed = (results as { isSuccess?: boolean }[]).some((r) => !r?.isSuccess);
            return of({
              ok: true as const,
              message: failed
                ? (res.message || 'บันทึกข่าวแล้ว แต่บางรูปอัปโหลดไม่สำเร็จ')
                : (res.message || 'บันทึกสำเร็จ'),
              newsId,
            });
          }),
        );
      }),
    ).subscribe({
      next: (result) => {
        this.isSaving.set(false);
        this.isUploadingGallery.set(false);
        if (result.ok) {
          this.toast.success(result.message);
          this.pendingGallery = [];
          this.saveSuccess.emit();
          this.onClose();
        } else {
          this.toast.error(result.message);
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        this.isUploadingGallery.set(false);
        this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาด');
      },
    });
  }

  private loadDetail(id: number): void {
    this.isLoading.set(true);
    this.api.getNewsById(id).subscribe({
      next: (detail) => {
        this.isLoading.set(false);
        this.applyDetail(detail);
      },
      error: () => {
        this.isLoading.set(false);
        this.toast.error('โหลดรายละเอียดไม่สำเร็จ');
      },
    });
  }

  private applyDetail(d: NewsDetail): void {
    this.form = {
      ...this.emptyForm(),
      id: d.id,
      name: d.name ?? '',
      title: d.title ?? '',
      titleEn: d.titleEn ?? '',
      summary: d.summary ?? '',
      body: d.body ?? '',
      bodyEn: d.bodyEn ?? '',
      coverImageUrl: d.coverImageUrl ?? '',
      categoryId: d.categoryId,
      visibility: d.visibility === 'internal' ? 'internal' : 'public',
      status: d.status || 'draft',
      siteId: d.siteId ?? null,
      publishFrom: this.toLocalInput(d.publishFrom),
      publishTo: this.toLocalInput(d.publishTo),
      pin: !!d.pin,
      sortOrder: d.sortOrder ?? 0,
      linkUrl: d.linkUrl ?? '',
      linkLabel: d.linkLabel ?? '',
      showOnGlobal: d.showOnGlobal !== false,
      userId: 0,
      menuId: 0,
    };
    this.coverBase64 = null;
    this.coverPreview.set(d.coverImageUrl || null);
    this.galleryImages.set(d.images ?? []);
    this.pendingGallery = [];
    this.searchCategoryText = this.selectedCategoryLabel;
    this.searchSiteText = this.selectedSiteLabel;
  }

  private toLocalInput(value?: string | null): string {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  formatDateTimeDisplay(value?: string | null): string {
    const raw = (value || '').trim();
    if (!raw) return 'เลือกวันที่';

    const [datePart, timePart = ''] = raw.split('T');
    const [y, m, d] = datePart.split('-');
    if (!y || !m || !d) return raw;

    const hm = timePart.slice(0, 5);
    return hm ? `${d}/${m}/${y} ${hm}` : `${d}/${m}/${y}`;
  }

  private emptyForm(): ManageNewsPayload {
    return {
      userId: 0,
      menuId: 0,
      id: 0,
      name: '',
      title: '',
      titleEn: '',
      summary: '',
      body: '',
      bodyEn: '',
      coverImageUrl: '',
      coverImageBase64: null,
      categoryId: 0,
      visibility: 'public',
      status: 'draft',
      siteId: null,
      publishFrom: '',
      publishTo: '',
      pin: false,
      sortOrder: 0,
      linkUrl: '',
      linkLabel: '',
      showOnGlobal: true,
    };
  }

  private resolveUserId(): number {
    const local = localStorage.getItem('userId');
    if (local) return Number(local);
    return Number(this.cookieService.get('user_id') || 0);
  }

  get canShowActivityHistory(): boolean {
    return (this.editId ?? 0) > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
